import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { Eye, EyeOff } from "lucide-react";
import { API_BASE_URL } from "../config/api";

type View = "login" | "updatePassword" | "forgotPassword";

const LoginPage: React.FC = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const { login, user } = useAuth();

    // View state
    const [view, setView] = useState<View>("login");

    // Update password state
    const [updateEmail, setUpdateEmail] = useState("");
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [updateError, setUpdateError] = useState("");
    const [updateSuccess, setUpdateSuccess] = useState("");
    const [isUpdating, setIsUpdating] = useState(false);

    // Forgot password state
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotOtp, setForgotOtp] = useState("");
    const [forgotNewPassword, setForgotNewPassword] = useState("");
    const [forgotConfirmPassword, setForgotConfirmPassword] = useState("");
    const [forgotStep, setForgotStep] = useState<"email" | "reset">("email");
    const [forgotError, setForgotError] = useState("");
    const [forgotSuccess, setForgotSuccess] = useState("");
    const [isForgotLoading, setIsForgotLoading] = useState(false);

    // Eye toggle state
    const [showLoginPassword, setShowLoginPassword] = useState(false);
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showForgotNewPassword, setShowForgotNewPassword] = useState(false);
    const [showForgotConfirmPassword, setShowForgotConfirmPassword] = useState(false);

    useEffect(() => {
        if (user) {
            navigate("/");
        }
    }, [user, navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError("");

        try {
            const response = await fetch(`${API_BASE_URL}/api/user/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, password }),
            });

            const data = await response.json();

            if (response.ok) {
                localStorage.setItem("memberUuid", data.memberUuid);
                const success = await login(email, password);
                if (success) {
                    navigate("/");
                } else {
                    setError("Authentication failed. Please try again.");
                }
            } else {
                setError(data.message || "Invalid credentials. Please try again.");
            }
        } catch (err) {
            console.error("Login error:", err);
            setError("An error occurred during login. Please try again.");
        }

        setIsLoading(false);
    };

    const handleUpdatePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsUpdating(true);
        setUpdateError("");
        setUpdateSuccess("");

        try {
            const response = await fetch(`${API_BASE_URL}/api/user/passwordUpdate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: updateEmail, oldPassword, newPassword }),
            });

            const data = await response.json();

            if (response.ok) {
                setUpdateSuccess("Password updated successfully. Please log in.");
                setTimeout(() => {
                    setView("login");
                    setUpdateEmail("");
                    setOldPassword("");
                    setNewPassword("");
                    setUpdateSuccess("");
                }, 2000);
            } else {
                setUpdateError(data.error || "Failed to update password.");
            }
        } catch (err) {
            console.error("Update password error:", err);
            setUpdateError("An error occurred. Please try again.");
        }

        setIsUpdating(false);
    };

    // Step 1: Request OTP
    const handleRequestOtp = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsForgotLoading(true);
        setForgotError("");
        setForgotSuccess("");

        try {
            const response = await fetch(`${API_BASE_URL}/api/user/request-password-reset`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: forgotEmail }),
            });

            const data = await response.json();

            if (response.ok) {
                setForgotSuccess("OTP sent to your email. Valid for 10 minutes.");
                setForgotStep("reset");
            } else {
                setForgotError(data.error || "Failed to send OTP.");
            }
        } catch (err) {
            console.error("Request OTP error:", err);
            setForgotError("An error occurred. Please try again.");
        }

        setIsForgotLoading(false);
    };

    // Step 2: Reset Password with OTP
    const handleResetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsForgotLoading(true);
        setForgotError("");
        setForgotSuccess("");

        if (forgotNewPassword !== forgotConfirmPassword) {
            setForgotError("Passwords do not match.");
            setIsForgotLoading(false);
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/api/user/reset-password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email: forgotEmail,
                    otp: forgotOtp,
                    password: forgotNewPassword,
                    confirmPassword: forgotConfirmPassword,
                }),
            });

            const data = await response.json();

            if (response.ok) {
                setForgotSuccess("Password reset successfully. Please log in.");
                setTimeout(() => {
                    resetForgotState();
                    setView("login");
                }, 2000);
            } else {
                setForgotError(data.error || "Failed to reset password.");
            }
        } catch (err) {
            console.error("Reset password error:", err);
            setForgotError("An error occurred. Please try again.");
        }

        setIsForgotLoading(false);
    };

    const resetForgotState = () => {
        setForgotEmail("");
        setForgotOtp("");
        setForgotNewPassword("");
        setForgotConfirmPassword("");
        setForgotStep("email");
        setForgotError("");
        setForgotSuccess("");
    };

    const goBackToLogin = () => {
        setView("login");
        setError("");
        resetForgotState();
        setUpdateError("");
        setUpdateSuccess("");
    };

    if (user) return null;

    return (
        <div className="flex flex-col justify-center items-center min-h-screen bg-black space-y-6 px-4">
            <img src="/Black BG - Logo.png" alt="SecuredStitch Logo" className="h-12" />

            <div className="max-w-sm w-full bg-gray-800 p-6 rounded-lg shadow-lg">
                {/* ─── LOGIN VIEW ─── */}
                {view === "login" && (
                    <>
                        <h2 className="text-2xl font-semibold text-center mb-4 text-white">Login</h2>
                        <form onSubmit={handleSubmit}>
                            {/* Email */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-300 mb-2" htmlFor="email">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    className="w-full px-4 py-2 border border-gray-600 rounded-md bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>

                            {/* Password */}
                            <div className="mb-2">
                                <label className="block text-sm font-medium text-gray-300 mb-2" htmlFor="password">
                                    Password
                                </label>
                                <div className="relative">
                                    <input
                                        type={showLoginPassword ? "text" : "password"}
                                        id="password"
                                        className="w-full px-4 py-2 border border-gray-600 rounded-md bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                                        className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-200"
                                    >
                                        {showLoginPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                            </div>

                            {/* Forgot Password link - right aligned above login button */}
                            <div className="flex justify-end mb-4">
                                <button
                                    type="button"
                                    onClick={() => {
                                        resetForgotState();
                                        setView("forgotPassword");
                                    }}
                                    className="text-xs text-blue-400 hover:text-blue-300 underline"
                                >
                                    Forgot Password?
                                </button>
                            </div>

                            {error && <div className="text-red-500 text-sm mb-4">{error}</div>}

                            <button
                                type="submit"
                                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                disabled={isLoading}
                            >
                                {isLoading ? "Logging in..." : "Login"}
                            </button>
                        </form>

                        <div className="mt-4 text-center">
                            <button
                                onClick={() => setView("updatePassword")}
                                className="text-xs text-gray-400 hover:text-blue-400 underline"
                            >
                                Update Password
                            </button>
                        </div>
                    </>
                )}

                {/* ─── UPDATE PASSWORD VIEW ─── */}
                {view === "updatePassword" && (
                    <>
                        <h2 className="text-2xl font-semibold text-center mb-4 text-white">Update Password</h2>
                        <form onSubmit={handleUpdatePassword}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-300 mb-2" htmlFor="updateEmail">
                                    Email
                                </label>
                                <input
                                    type="email"
                                    id="updateEmail"
                                    className="w-full px-4 py-2 border border-gray-600 rounded-md bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={updateEmail}
                                    onChange={(e) => setUpdateEmail(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-300 mb-2" htmlFor="oldPassword">
                                    Old Password
                                </label>
                                <div className="relative">
                                    <input
                                        type={showOldPassword ? "text" : "password"}
                                        id="oldPassword"
                                        className="w-full px-4 py-2 border border-gray-600 rounded-md bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                                        value={oldPassword}
                                        onChange={(e) => setOldPassword(e.target.value)}
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowOldPassword(!showOldPassword)}
                                        className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-200"
                                    >
                                        {showOldPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-300 mb-2" htmlFor="newPassword">
                                    New Password
                                </label>
                                <div className="relative">
                                    <input
                                        type={showNewPassword ? "text" : "password"}
                                        id="newPassword"
                                        className="w-full px-4 py-2 border border-gray-600 rounded-md bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-200"
                                    >
                                        {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                    </button>
                                </div>
                            </div>

                            {updateError && <div className="text-red-500 text-sm mb-4">{updateError}</div>}
                            {updateSuccess && <div className="text-green-500 text-sm mb-4">{updateSuccess}</div>}

                            <button
                                type="submit"
                                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                disabled={isUpdating}
                            >
                                {isUpdating ? "Updating..." : "Update Password"}
                            </button>
                        </form>

                        <div className="mt-4 text-center">
                            <button
                                onClick={goBackToLogin}
                                className="text-xs text-gray-400 hover:text-blue-400 underline"
                            >
                                Back to Login
                            </button>
                        </div>
                    </>
                )}

                {/* ─── FORGOT PASSWORD VIEW ─── */}
                {view === "forgotPassword" && (
                    <>
                        <h2 className="text-2xl font-semibold text-center mb-1 text-white">Forgot Password</h2>

                        {/* Step indicator */}
                        <p className="text-center text-xs text-gray-400 mb-4">
                            {forgotStep === "email"
                                ? "Step 1 of 2 — Enter your email to receive an OTP"
                                : "Step 2 of 2 — Enter the OTP and your new password"}
                        </p>

                        {/* ── Step 1: Email form ── */}
                        {forgotStep === "email" && (
                            <form onSubmit={handleRequestOtp}>
                                <div className="mb-4">
                                    <label
                                        className="block text-sm font-medium text-gray-300 mb-2"
                                        htmlFor="forgotEmail"
                                    >
                                        Email
                                    </label>
                                    <input
                                        type="email"
                                        id="forgotEmail"
                                        className="w-full px-4 py-2 border border-gray-600 rounded-md bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        value={forgotEmail}
                                        onChange={(e) => setForgotEmail(e.target.value)}
                                        placeholder="Enter your registered email"
                                        required
                                    />
                                </div>

                                {forgotError && <div className="text-red-500 text-sm mb-4">{forgotError}</div>}
                                {forgotSuccess && <div className="text-green-500 text-sm mb-4">{forgotSuccess}</div>}

                                <button
                                    type="submit"
                                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    disabled={isForgotLoading}
                                >
                                    {isForgotLoading ? "Sending OTP..." : "Send OTP"}
                                </button>
                            </form>
                        )}

                        {/* ── Step 2: OTP + New Password form ── */}
                        {forgotStep === "reset" && (
                            <form onSubmit={handleResetPassword}>
                                {/* Email (read-only) */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                                    <input
                                        type="email"
                                        className="w-full px-4 py-2 border border-gray-600 rounded-md bg-gray-600 text-gray-300 cursor-not-allowed"
                                        value={forgotEmail}
                                        readOnly
                                    />
                                </div>

                                {/* OTP */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-300 mb-2" htmlFor="forgotOtp">
                                        OTP
                                    </label>
                                    <input
                                        type="text"
                                        id="forgotOtp"
                                        maxLength={6}
                                        className="w-full px-4 py-2 border border-gray-600 rounded-md bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 tracking-widest text-center text-lg"
                                        value={forgotOtp}
                                        onChange={(e) => setForgotOtp(e.target.value.replace(/\D/g, ""))}
                                        placeholder="_ _ _ _ _ _"
                                        required
                                    />
                                </div>

                                {/* New Password */}
                                <div className="mb-4">
                                    <label
                                        className="block text-sm font-medium text-gray-300 mb-2"
                                        htmlFor="forgotNewPassword"
                                    >
                                        New Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showForgotNewPassword ? "text" : "password"}
                                            id="forgotNewPassword"
                                            className="w-full px-4 py-2 border border-gray-600 rounded-md bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                                            value={forgotNewPassword}
                                            onChange={(e) => setForgotNewPassword(e.target.value)}
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowForgotNewPassword(!showForgotNewPassword)}
                                            className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-200"
                                        >
                                            {showForgotNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </div>
                                </div>

                                {/* Confirm Password */}
                                <div className="mb-4">
                                    <label
                                        className="block text-sm font-medium text-gray-300 mb-2"
                                        htmlFor="forgotConfirmPassword"
                                    >
                                        Confirm New Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showForgotConfirmPassword ? "text" : "password"}
                                            id="forgotConfirmPassword"
                                            className="w-full px-4 py-2 border border-gray-600 rounded-md bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                                            value={forgotConfirmPassword}
                                            onChange={(e) => setForgotConfirmPassword(e.target.value)}
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowForgotConfirmPassword(!showForgotConfirmPassword)}
                                            className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-200"
                                        >
                                            {showForgotConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </div>
                                </div>

                                {forgotError && <div className="text-red-500 text-sm mb-4">{forgotError}</div>}
                                {forgotSuccess && <div className="text-green-500 text-sm mb-4">{forgotSuccess}</div>}

                                <button
                                    type="submit"
                                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    disabled={isForgotLoading}
                                >
                                    {isForgotLoading ? "Resetting..." : "Reset Password"}
                                </button>

                                {/* Resend OTP */}
                                <div className="mt-3 text-center">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setForgotStep("email");
                                            setForgotOtp("");
                                            setForgotNewPassword("");
                                            setForgotConfirmPassword("");
                                            setForgotError("");
                                            setForgotSuccess("");
                                        }}
                                        className="text-xs text-gray-400 hover:text-blue-400 underline"
                                    >
                                        Didn't receive OTP? Go back & resend
                                    </button>
                                </div>
                            </form>
                        )}

                        <div className="mt-4 text-center">
                            <button
                                onClick={goBackToLogin}
                                className="text-xs text-gray-400 hover:text-blue-400 underline"
                            >
                                Back to Login
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default LoginPage;
